import { useCallback, useEffect, useRef, useState } from "react";

import { guacHttpClient } from "@/lib/http";
import {
  SadieVoiceCaption,
  sadieVoiceSimulator,
} from "@/lib/sadieVoiceSimulator";
import {
  analyserLevel,
  createVoiceGapTracker,
  VoiceGap,
  VoiceGapTracker,
} from "@/lib/voiceGapTracker";
import {
  HttpError,
  HttpResponse,
  SadieVoiceEngine,
  SadieVoiceSession,
  SadieVoiceSessionRequest,
} from "@/types";

export type SadieVoiceCallStatus =
  | "idle"
  | "connecting"
  | "active"
  | "ending"
  | "ended"
  | "failed";

// Something the page sends once the session is ready, with the transcript
// line that says so (none for a silent one).
export interface SadieVoiceReadyEvent {
  // Absent for a transcript line only.
  event?: Record<string, unknown>;
  label?: string;
}

export type SadieVoiceCallOptions = Omit<SadieVoiceSessionRequest, "sdp"> & {
  // Sent in order once commands are accepted: Live's pushed background
  // notes, or Realtime's response.create when Sadie speaks first.
  onReady: SadieVoiceReadyEvent[];
};

// The offer goes out once ICE gathering finishes or after this long, with
// whatever candidates it has. Same cap as the Staffer app.
const ICE_GATHER_MS = 1500;
// Live acknowledges session.close with session.closed; stop waiting after
// this long.
const CLOSE_WAIT_MS = 5000;
// How often the response-gap meter samples audio levels.
const METER_INTERVAL_MS = 50;
// After end_call, hang up once Sadie's goodbye has had time to play out.
const END_CALL_DELAY_MS = 2500;

const waitForIceGathering = (pc: RTCPeerConnection) =>
  new Promise<void>((resolve) => {
    if (pc.iceGatheringState === "complete") {
      resolve();
      return;
    }
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", onChange);
      clearTimeout(timer);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    const timer = setTimeout(done, ICE_GATHER_MS);
    pc.addEventListener("icegatheringstatechange", onChange);
  });

const errorMessage = (error: unknown): string => {
  const httpError = error as HttpError<{ message?: string }>;
  if (httpError.response?.data.message) return httpError.response.data.message;
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return "Microphone access was blocked. Allow it for this site and try again.";
  }
  return error instanceof Error ? error.message : "The call failed to start.";
};

/**
 * One WebRTC voice call with Sadie on OpenAI Live or Realtime. guac trades the
 * SDP offer for OpenAI's answer; after that, audio rides the peer connection
 * and JSON events ride the `oai-events` data channel, straight to OpenAI.
 */
export function useSadieVoiceCall() {
  const [status, setStatus] = useState<SadieVoiceCallStatus>("idle");
  const [captions, setCaptions] = useState<SadieVoiceCaption[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Errors OpenAI reports mid-call. The call carries on.
  const [warning, setWarning] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [session, setSession] = useState<SadieVoiceSession | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  // Response gaps so far, oldest first.
  const [gaps, setGaps] = useState<VoiceGap[]>([]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const engineRef = useRef<SadieVoiceEngine>("live");
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const meterTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gapTrackerRef = useRef<VoiceGapTracker | null>(null);
  const endCallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every start and release, so a start that is still awaiting
  // after a hang-up drops what it gets back.
  const attemptRef = useRef(0);

  const release = useCallback(() => {
    attemptRef.current += 1;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    if (endCallTimerRef.current) clearTimeout(endCallTimerRef.current);
    endCallTimerRef.current = null;
    if (meterTimerRef.current) clearInterval(meterTimerRef.current);
    meterTimerRef.current = null;
    void audioContextRef.current?.close();
    audioContextRef.current = null;
    gapTrackerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
    dcRef.current?.close();
    dcRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
  }, []);

  // Hang up if the page goes away mid-call.
  useEffect(() => release, [release]);

  const send = (event: Record<string, unknown>) => {
    if (dcRef.current?.readyState === "open") {
      dcRef.current.send(JSON.stringify(event));
    }
  };

  const addLine = (speaker: "text" | "event", text: string) => {
    setCaptions((current) =>
      sadieVoiceSimulator.appendLine(current, speaker, text),
    );
  };

  const start = async ({ onReady, ...request }: SadieVoiceCallOptions) => {
    release();
    const attempt = attemptRef.current;
    const isCurrent = () => attempt === attemptRef.current;
    engineRef.current = request.engine;
    setStatus("connecting");
    setCaptions([]);
    setError(null);
    setWarning(null);
    setIsMuted(false);
    setSession(null);
    setStartedAt(null);
    setGaps([]);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (!isCurrent()) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        return;
      }
      streamRef.current = stream;

      // Audio levels for the response-gap meter: the mic now, Sadie once her
      // track arrives.
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      void audioContext.resume();
      const analyser = () => {
        const node = audioContext.createAnalyser();
        node.fftSize = 1024;
        return node;
      };
      const micAnalyser = analyser();
      audioContext.createMediaStreamSource(stream).connect(micAnalyser);
      const sadieAnalyser = analyser();
      const levels = new Float32Array(micAnalyser.fftSize);
      const tracker = createVoiceGapTracker();
      gapTrackerRef.current = tracker;
      meterTimerRef.current = setInterval(() => {
        const gap = tracker.tick(
          performance.now(),
          analyserLevel(micAnalyser, levels),
          analyserLevel(sadieAnalyser, levels),
        );
        if (gap) setGaps((current) => [...current, gap]);
      }, METER_INTERVAL_MS);

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      audioRef.current ??= Object.assign(document.createElement("audio"), {
        autoplay: true,
      });
      pc.ontrack = (e) => {
        if (audioRef.current) audioRef.current.srcObject = e.streams[0];
        audioContext
          .createMediaStreamSource(e.streams[0])
          .connect(sadieAnalyser);
      };
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      pc.onconnectionstatechange = () => {
        if (!isCurrent()) return;
        if (pc.connectionState === "connected") {
          setStatus("active");
          setStartedAt(Date.now());
        } else if (pc.connectionState === "failed") {
          release();
          setError("The connection to OpenAI dropped.");
          setStatus("failed");
        }
      };

      // Created before the offer, or it is left out of the SDP.
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      let opened = false;
      const open = () => {
        if (opened) return;
        opened = true;
        for (const { event, label } of onReady) {
          if (event) send(event);
          if (label) addLine("event", label);
        }
      };

      // Live's Responses backend runs send_text and end_call, and the page
      // answers both: nothing is texted, and end_call hangs up. Like
      // LiveSidebandService, the backend continues (response.create) once
      // per round, after its response is done and every call is answered.
      const lookups = new Map<
        string,
        { pending: Set<string>; finished: boolean; calls: number }
      >();
      const continueIfDone = (key: string) => {
        const lookup = lookups.get(key);
        if (!lookup?.finished || lookup.pending.size || !lookup.calls) return;
        lookups.delete(key);
        if (!endCallTimerRef.current) send({ type: "response.create" });
      };
      const answerTool = (
        key: string,
        callId: string,
        name: string,
        args: Record<string, unknown>,
      ) => {
        const lookup = lookups.get(key) ?? {
          pending: new Set<string>(),
          finished: false,
          calls: 0,
        };
        lookups.set(key, lookup);
        if (lookup.pending.has(callId)) return;
        lookup.pending.add(callId);
        lookup.calls += 1;
        let output = "Done.";
        if (name === "send_text") {
          const text = typeof args.text === "string" ? args.text : "";
          addLine("text", text || "(empty text)");
          output = "Sent.";
        } else if (name === "end_call") {
          addLine("event", "Sadie's backend ended the call");
          output = "The call is ending.";
          endCallTimerRef.current ??= setTimeout(() => {
            hangUp();
          }, END_CALL_DELAY_MS);
        } else {
          output = `Unknown tool ${name}.`;
        }
        send({
          type: "response.item.create",
          item: { type: "function_call_output", call_id: callId, output },
        });
        lookup.pending.delete(callId);
        continueIfDone(key);
      };
      dc.onmessage = (message: MessageEvent<string>) => {
        if (!isCurrent()) return;
        let event: Record<string, unknown>;
        try {
          event = JSON.parse(message.data) as Record<string, unknown>;
        } catch {
          return;
        }
        // Realtime's turn commit splits the gap into turn detection and the
        // model's reply.
        if (event.type === "input_audio_buffer.committed") {
          gapTrackerRef.current?.markCommitted(performance.now());
        }
        const engine = engineRef.current;
        setCaptions((current) =>
          sadieVoiceSimulator.captionsAfter(engine, current, event),
        );
        const backend =
          engine === "live" ? sadieVoiceSimulator.backendEventOf(event) : null;
        if (backend?.kind === "started") {
          lookups.set(backend.key, {
            pending: new Set(),
            finished: false,
            calls: 0,
          });
        } else if (backend?.kind === "tool") {
          answerTool(backend.key, backend.callId, backend.name, backend.args);
        } else if (backend?.kind === "ended") {
          if (backend.status !== "completed") {
            setWarning(
              `Sadie's backend ${backend.status}${backend.error ? `: ${backend.error}` : "."}`,
            );
          }
          const lookup = lookups.get(backend.key);
          if (lookup) {
            lookup.finished = true;
            continueIfDone(backend.key);
          }
        }
        const signal = sadieVoiceSimulator.signalOf(engine, event);
        if (!signal) return;
        if (signal.type === "ready") open();
        else if (signal.type === "closed") {
          release();
          setStatus("ended");
        } else setWarning(signal.message);
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);
      if (!isCurrent()) return;

      const res = await guacHttpClient.post<
        SadieVoiceSession,
        HttpResponse<SadieVoiceSession>
      >(
        "relationships/simulate/voice/session",
        {
          ...request,
          sdp: pc.localDescription?.sdp ?? offer.sdp ?? "",
        } satisfies SadieVoiceSessionRequest,
        { suppressGlobalError: true },
      );
      if (!isCurrent()) return;
      setSession(res.data);
      await pc.setRemoteDescription({ type: "answer", sdp: res.data.sdp });
    } catch (e) {
      if (!isCurrent()) return;
      release();
      setError(errorMessage(e));
      setStatus("failed");
    }
  };

  const hangUp = () => {
    // Live says goodbye properly: session.close, then wait for
    // session.closed (handled in onmessage). Realtime ends when the peer
    // connection closes.
    if (engineRef.current === "live" && dcRef.current?.readyState === "open") {
      streamRef.current?.getTracks().forEach((track) => {
        track.enabled = false;
      });
      send({ type: "session.close" });
      setStatus("ending");
      closeTimerRef.current = setTimeout(() => {
        release();
        setStatus("ended");
      }, CLOSE_WAIT_MS);
      return;
    }
    release();
    setStatus("ended");
  };

  /** Live only: a mid-call note (thinking.append) or guard (instructions.append). */
  const pushNote = (
    kind: "thinking" | "instructions",
    content: string,
    label: string,
  ) => {
    send({
      type:
        kind === "thinking"
          ? "session.thinking.append"
          : "session.instructions.append",
      content,
      delegation_id: null,
    });
    addLine("event", label);
  };

  const toggleMute = () => {
    const muted = !isMuted;
    streamRef.current?.getTracks().forEach((track) => {
      track.enabled = !muted;
    });
    if (engineRef.current === "live") {
      send({
        type: muted ? "session.input_audio.mute" : "session.input_audio.unmute",
      });
    }
    setIsMuted(muted);
  };

  return {
    captions,
    error,
    gaps,
    hangUp,
    isMuted,
    pushNote,
    session,
    start,
    startedAt,
    status,
    toggleMute,
    warning,
  };
}
